import React, { useEffect, useState, useCallback } from "react";
import { PlusCircle, ChevronDown, ChevronUp, Check, X } from "lucide-react";
import api, { apiError } from "../../api";
import { useLang } from "../../i18n";
import MagicalCard from "../../components/MagicalCard";
import Avatar from "../../components/Avatar";
import { Icon, EmptyState, SkeletonPanel } from "../../components/common";
import { ChallengeStatusChip, daysLeftText } from "../../components/WeeklyChallengeCard";
import ChallengeFormModal from "../../components/ChallengeFormModal";
import { fmtRelative } from "../../utils";
import { toast } from "sonner";

function Participations({ challengeId }) {
  const { t, lang } = useLang();
  const [parts, setParts] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/staff/challenges/${challengeId}/participations`);
    setParts(data.participations);
  }, [challengeId]);

  useEffect(() => { load(); }, [load]);

  const review = async (p, action) => {
    try {
      await api.post(`/staff/participations/${p.id}/review`, { action });
      toast.success(action === "approve" ? t("challenges.completed_toast") : "✓");
      load();
    } catch (e) {
      toast.error(apiError(e, t));
    }
  };

  if (!parts) return <SkeletonPanel lines={2} />;
  if (parts.length === 0) return <p className="text-sm text-[#A5A0D6] font-bold py-3 text-center">{t("teacher.no_approvals")}</p>;
  return (
    <ul className="space-y-2 mt-3">
      {parts.map((p) => (
        <li key={p.id} data-testid={`participation-${p.id}`} className="flex items-center gap-3 rounded-xl bg-[#0D0B38]/60 border border-white/10 px-3 py-2.5">
          <Avatar src={p.student_avatar} name={lang === "ar" ? p.student_name_ar : p.student_name_en} size="xs" />
          <span className="flex-1 text-sm font-bold">{lang === "ar" ? p.student_name_ar : p.student_name_en}</span>
          <span className="text-[10px] text-[#A5A0D6] font-bold">{fmtRelative(p.submitted_at, lang)}</span>
          <ChallengeStatusChip status={p.status === "awaiting" ? "awaiting" : p.status === "approved" ? "completed" : "rejected"} />
          {p.status === "awaiting" && (
            <div className="flex gap-1.5">
              <button data-testid={`approve-${p.id}`} onClick={() => review(p, "approve")}
                className="w-9 h-9 rounded-xl bg-[#48D276]/15 border border-[#48D276]/40 text-[#48D276] flex items-center justify-center hover:bg-[#48D276]/30 transition-colors" title={t("teacher.approve")}>
                <Check className="w-4 h-4" />
              </button>
              <button data-testid={`reject-${p.id}`} onClick={() => review(p, "reject")}
                className="w-9 h-9 rounded-xl bg-[#EF4D9A]/15 border border-[#EF4D9A]/40 text-[#FF8FB1] flex items-center justify-center hover:bg-[#EF4D9A]/30 transition-colors" title={t("teacher.reject")}>
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

export default function TeacherChallengesPage() {
  const { t, pick } = useLang();
  const [challenges, setChallenges] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/challenges");
    setChallenges(data.challenges);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="teacher-challenges-page">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl sm:text-3xl font-black">{t("nav.challenges")}</h2>
        <button data-testid="create-challenge-btn" onClick={() => setFormOpen(true)} className="btn-magic text-sm">
          <PlusCircle className="w-4 h-4" /> {t("teacher.create_challenge")}
        </button>
      </div>
      {!challenges ? (
        <SkeletonPanel lines={5} />
      ) : challenges.length === 0 ? (
        <MagicalCard hover={false}><EmptyState icon="Target" title={t("challenges.empty")} testId="challenges-empty" /></MagicalCard>
      ) : (
        challenges.map((ch) => (
          <MagicalCard key={ch.id} testId={`teacher-challenge-${ch.id}`} className="!pt-5" hover={false}>
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#EF4D9A] to-[#FF63B5] flex items-center justify-center shrink-0 glow-pink">
                <Icon name={ch.icon || "Target"} className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-black text-lg">{pick(ch, "title")}</h4>
                  <ChallengeStatusChip status={ch.status} />
                  {ch.awaiting_count > 0 && (
                    <span className="text-[11px] font-extrabold px-2.5 py-1 rounded-full bg-[#FFC83D]/15 text-[#FFC83D] border border-[#FFC83D]/40">
                      {t("teacher.approvals")}: {ch.awaiting_count}
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#D8D4F2] font-semibold mt-1">{pick(ch, "desc")}</p>
                <div className="flex items-center gap-4 mt-2 text-sm font-bold text-[#A5A0D6]">
                  <span className="text-[#FFC83D]">+{ch.points}</span>
                  {daysLeftText(ch, t) && <span className="text-[#27C9EE]">{daysLeftText(ch, t)}</span>}
                </div>
                <button data-testid={`toggle-parts-${ch.id}`} onClick={() => setExpanded(expanded === ch.id ? null : ch.id)}
                  className="mt-3 text-sm font-extrabold text-[#9C42F5] hover:text-white flex items-center gap-1 transition-colors">
                  {expanded === ch.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  {t("teacher.awaiting_review")} ({ch.participants})
                </button>
                {expanded === ch.id && <Participations challengeId={ch.id} />}
              </div>
            </div>
          </MagicalCard>
        ))
      )}
      <ChallengeFormModal open={formOpen} onClose={() => setFormOpen(false)} onDone={load} />
    </div>
  );
}
